(() => {
  if (customElements.get('accessible-carousel')) return;
  class AccessibleCarousel extends HTMLElement {
    connectedCallback() {
      this.disconnectController?.abort();
      this.disconnectController = new AbortController();
      const options = { signal: this.disconnectController.signal };
      this.slides = [...this.querySelectorAll(this.dataset.slideSelector)];
      if (!this.slides.length) return;
      this.current = Math.min(this.current || 0, this.slides.length - 1);
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.userPaused = this.userPaused ?? this.reducedMotion.matches;
      this.playButton = this.querySelector('[data-carousel-play]');
      this.dots = [...this.querySelectorAll('[data-carousel-dot]')];
      this.addEventListener('click', (event) => {
        if (event.target.closest('[data-carousel-prev]')) this.showSlide(this.current - 1);
        else if (event.target.closest('[data-carousel-next]')) this.showSlide(this.current + 1);
        else if (event.target.closest('[data-carousel-dot]')) this.showSlide(Number(event.target.closest('[data-carousel-dot]').dataset.carouselDot));
        else if (event.target.closest('[data-carousel-play]')) this.userPaused = !this.userPaused;
        else return;
        this.updatePlayback();
      }, options);
      this.addEventListener('mouseenter', () => { this.hovered = true; this.updatePlayback(); }, options);
      this.addEventListener('mouseleave', () => { this.hovered = false; this.updatePlayback(); }, options);
      this.addEventListener('focusin', () => this.updatePlayback(), options);
      this.addEventListener('focusout', () => queueMicrotask(() => this.updatePlayback()), options);
      this.reducedMotion.addEventListener('change', () => {
        if (this.reducedMotion.matches) this.userPaused = true;
        this.updatePlayback();
      }, options);
      document.addEventListener('visibilitychange', () => this.updatePlayback(), options);
      const track = this.querySelector('[data-carousel-track]') || this;
      // Touch swipes change only this card's slide. Let vertical gestures and
      // pinch zoom stay native; suppress the synthetic link click after a swipe.
      // A fresh touch clears suppression so a deliberate next tap still opens it.
      if (this.slides.length > 1) {
        let touchStart = null;
        let ignoreClickUntil = 0;
        track.addEventListener('pointerdown', (event) => {
          if (event.pointerType !== 'touch') return;
          if (!event.isPrimary) { touchStart = null; return; }
          if (!window.matchMedia('(max-width: 749px)').matches) return;
          ignoreClickUntil = 0;
          touchStart = { id: event.pointerId, x: event.clientX, y: event.clientY };
        }, { ...options, passive: true });
        track.addEventListener('pointercancel', () => { touchStart = null; }, { ...options, passive: true });
        track.addEventListener('pointerup', (event) => {
          if (!touchStart || event.pointerId !== touchStart.id) return;
          const dx = event.clientX - touchStart.x;
          const dy = event.clientY - touchStart.y;
          touchStart = null;
          if (Math.abs(dx) < 45 || Math.abs(dx) < Math.abs(dy) * 1.25) return;
          ignoreClickUntil = performance.now() + 500;
          if (dx < 0) this.showSlide(this.current + 1); else this.showSlide(this.current - 1);
        }, { ...options, passive: true });
        track.addEventListener('click', (event) => {
          if (event.detail > 0 && performance.now() < ignoreClickUntil) {
            event.preventDefault();
            event.stopPropagation();
          }
        }, { ...options, capture: true });
      }


      this.showSlide(this.current);
      this.updatePlayback();
    }

    disconnectedCallback() {
      clearInterval(this.timer);
      this.disconnectController?.abort();
    }

    showSlide(index) {
      this.current = (index + this.slides.length) % this.slides.length;
      this.slides.forEach((slide, i) => {
        const active = i === this.current;
        slide.hidden = !active;
        slide.inert = !active;
        slide.classList.toggle('is-active', active);
        slide.classList.toggle('active', active);
      });
      this.dots.forEach((dot, i) => {
        dot.classList.toggle('is-active', i === this.current);
        if (i === this.current) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
      });
    }

    updatePlayback() {
      clearInterval(this.timer);
      if (!this.isConnected) return;
      if (this.playButton) this.playButton.textContent = this.userPaused
        ? this.playButton.dataset.playLabel : this.playButton.dataset.pauseLabel;
      const playing = this.dataset.autoplay === 'true' && this.slides.length > 1 && !this.userPaused
        && !this.hovered && !this.contains(document.activeElement) && !document.hidden;
      const track = this.querySelector('[data-carousel-track]');
      track?.setAttribute('aria-live', playing ? 'off' : 'polite');
      if (playing) this.timer = setInterval(() => {
        if (this.contains(document.activeElement)) return;
        this.showSlide(this.current + 1);
      }, Math.max(1000, Number(this.dataset.speedMs) || 5000));
    }
  }
  customElements.define('accessible-carousel', AccessibleCarousel);
})();
